/*
 * What search & commands (D21, mobile 18) finds for a query: conversations, artifacts, files and
 * commands, each kind counted for its filter pill. Pure, for the dialog, the phone's screen and
 * the tests.
 */

export type SearchKind = 'conversations' | 'artifacts' | 'files' | 'commands' | 'archived';
export type SearchFilter = 'all' | SearchKind;

export const SEARCH_KINDS: readonly { kind: SearchKind; label: string }[] = [
  { kind: 'conversations', label: 'Conversations' },
  { kind: 'artifacts', label: 'Artifacts' },
  { kind: 'files', label: 'Files' },
  { kind: 'commands', label: 'Commands' },
  { kind: 'archived', label: 'Archived' },
];

export type SearchEntry = {
  id: string;
  kind: SearchKind;
  label: string;
  /** Shown after the label, muted ("Dashboard · crowdex", "Worktrees › Automatic cleanup"). */
  detail?: string | undefined;
  /** At the right (an age). */
  meta?: string | undefined;
  /** More text it is found by. */
  keywords?: readonly string[] | undefined;
  /** Always offered, whatever is typed (a command such as "New conversation"). */
  always?: boolean | undefined;
};

/** Lower case, without accents, so "acceleration" finds "Accélération". */
export function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase();
}

function entryMatches(entry: SearchEntry, query: string): boolean {
  const needle = normalize(query.trim());
  if (needle === '') return true;
  return [entry.label, entry.detail ?? '', ...(entry.keywords ?? [])].some((text) =>
    normalize(text).includes(needle),
  );
}

export type SearchResults<T extends SearchEntry> = {
  /** What the filter shows, by kind, in the kinds' order. */
  groups: { kind: SearchKind; label: string; entries: T[] }[];
  counts: Record<SearchKind, number>;
};

/**
 * The entries matching a query, grouped by kind; an empty query lists the latest of each kind
 * (at most `emptyLimit`), commands that are always offered stay.
 */
export function searchResults<T extends SearchEntry>(
  entries: readonly T[],
  query: string,
  filter: SearchFilter,
  { emptyLimit = 6 }: { emptyLimit?: number } = {},
): SearchResults<T> {
  const typed = query.trim() !== '';
  const counts: Record<SearchKind, number> = {
    conversations: 0,
    artifacts: 0,
    files: 0,
    commands: 0,
    archived: 0,
  };
  const byKind = new Map<SearchKind, T[]>();
  // A command always offered that the query does not name comes after those it names: typed
  // "Add repository", Enter runs Add repository…, not New conversation (M4).
  const offered: T[] = [];
  for (const entry of entries) {
    const matches = entryMatches(entry, query);
    if (!(entry.always === true && entry.kind === 'commands') && !matches) continue;
    if (typed && !matches) offered.push(entry);
    else add(entry);
  }
  for (const entry of offered) add(entry);
  function add(entry: T) {
    counts[entry.kind] += 1;
    const list = byKind.get(entry.kind) ?? [];
    list.push(entry);
    byKind.set(entry.kind, list);
  }
  const groups = SEARCH_KINDS.filter(({ kind }) => filter === 'all' || filter === kind).flatMap(
    ({ kind, label }) => {
      const list = byKind.get(kind) ?? [];
      const shown = typed || filter !== 'all' ? list : list.slice(0, emptyLimit);
      return shown.length === 0 ? [] : [{ kind, label, entries: shown }];
    },
  );
  return { groups, counts };
}

/** The label cut around the query, so the match can be shown in bold. */
export function highlightParts(text: string, query: string): { text: string; match: boolean }[] {
  const needle = normalize(query.trim());
  if (needle === '') return [{ text, match: false }];
  const haystack = normalize(text);
  const parts: { text: string; match: boolean }[] = [];
  let from = 0;
  // Accents removed keep one character per character, so indices line up.
  for (
    let index = haystack.indexOf(needle);
    index !== -1 && haystack.length === text.length;
    index = haystack.indexOf(needle, from)
  ) {
    if (index > from) parts.push({ text: text.slice(from, index), match: false });
    parts.push({ text: text.slice(index, index + needle.length), match: true });
    from = index + needle.length;
  }
  if (from < text.length) parts.push({ text: text.slice(from), match: false });
  return parts.length === 0 ? [{ text, match: false }] : parts;
}
